import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AllianceMemberView, AllianceSummary } from '@kaeliux/shared';
import { ALLIANCE_DESCRIPTION_MAX_LENGTH, ALLIANCE_MAX_MEMBERS, ALLIANCE_ROLE_LABELS, GUEST_ALLIANCE_MESSAGE } from '@kaeliux/shared';
import { hasAlliancePermission } from '@kaeliux/game-engine';
import { useAuthStore } from '../store/authStore';
import GuestLockedPage from '../components/auth/GuestLockedPage';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { cn } from '../utils/cn';
import { formatFullNumber } from '../utils/formatters';
import AllianceApplicationList from '../components/AllianceApplicationList';
import AllianceApplyModal from '../components/AllianceApplyModal';
import AllianceBrowseList from '../components/AllianceBrowseList';
import AllianceCreateForm from '../components/AllianceCreateForm';
import AllianceLeaderIcon from '../components/AllianceLeaderIcon';
import AllianceMemberTable from '../components/AllianceMemberTable';
import AllianceTeamPlanetTab from '../components/AllianceTeamPlanetTab';
import AllianceSunsTab from '../components/AllianceSunsTab';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Panel } from '../components/ui/Panel';
import { FIELD_BASE_CLASSES, FIELD_FOCUS_CLASSES } from '../components/ui/fieldStyles';
import {
    applyToAlliance,
    cancelAllianceApplication,
    createAlliance,
    disbandAlliance,
    getAlliance,
    kickAllianceMember,
    leaveAlliance,
    listAlliances,
    respondToAllianceApplication,
    setAllianceMemberRole,
    transferAllianceLeadership,
    updateAlliance,
} from '../services/alliance.service';

type Tab = 'overview' | 'members' | 'applications' | 'teamPlanet' | 'suns' | 'browse';

/** Server/mock rejections arrive as plain `Error`s; this is the single place that unwraps them. */
function errorMessage(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
}

export default function AlliancePage() {
    const { session } = useAuthStore();
    if (session?.isGuest) {
        return (
            <GuestLockedPage title="Alliance">{GUEST_ALLIANCE_MESSAGE}</GuestLockedPage>
        );
    }
    return <AllianceContent />;
}

function AllianceContent() {
    const { profile } = useAuthStore();
    const queryClient = useQueryClient();
    const { openDialog } = useConfirmDialog();

    const navigate = useNavigate();
    const [tab, setTab] = useState<Tab>('overview');
    const [search, setSearch] = useState('');
    /**
     * Which alliance is being "previewed", taken from the route
     * (`/alliance/:allianceId`) rather than component state — so an
     * alliance is linkable from anywhere (the ranking table's alliance
     * column, a shared URL) and the browser's back button works. Undefined
     * on `/alliance`, which shows the viewer's own alliance.
     */
    const { allianceId: routeAllianceId } = useParams<{ allianceId: string }>();
    const previewAllianceId = routeAllianceId ?? null;
    const [actionError, setActionError] = useState<string | null>(null);
    /**
     * Kept separate from `actionError`: the create form renders its own
     * inline message, so routing founding failures through the page-level
     * banner too would show the same sentence twice.
     */
    const [createError, setCreateError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [descriptionDraft, setDescriptionDraft] = useState<string | null>(null);
    /** The alliance whose apply-with-message modal is open, if any. */
    const [applyTarget, setApplyTarget] = useState<AllianceSummary | null>(null);

    const allianceQuery = useQuery({
        queryKey: ['alliance', previewAllianceId ?? 'mine'],
        queryFn: () => getAlliance(previewAllianceId ?? undefined),
        staleTime: 30_000,
    });

    const browseQuery = useQuery({
        queryKey: ['alliances', 'browse', search],
        queryFn: () => listAlliances(search),
        staleTime: 30_000,
    });

    const detail = allianceQuery.data ?? null;
    const viewerRole = detail?.viewerRole ?? null;
    const isMyAlliance = detail != null && viewerRole != null;
    const canReviewApplications = hasAlliancePermission(viewerRole, 'manageApplications');
    const canEditProfile = hasAlliancePermission(viewerRole, 'manageProfile');

    // A tab that stops being available (leaving an alliance while on
    // Members, or previewing a foreign alliance whose applications you
    // can't see) must not leave the page on a dead tab.
    useEffect(() => {
        if (tab === 'applications' && !canReviewApplications) setTab('overview');
        if (tab === 'members' && !detail) setTab('overview');
        if ((tab === 'teamPlanet' || tab === 'suns') && !isMyAlliance) setTab('overview');
    }, [tab, canReviewApplications, detail, isMyAlliance]);

    async function runAction<T>(key: string | null, action: () => Promise<T>, fallback: string): Promise<T | undefined> {
        setActionError(null);
        setBusyId(key);
        try {
            return await action();
        } catch (error) {
            setActionError(errorMessage(error, fallback));
            return undefined;
        } finally {
            setBusyId(null);
        }
    }

    /** Both queries feed every view, so every mutation invalidates both. */
    async function refreshAll() {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['alliance'] }),
            queryClient.invalidateQueries({ queryKey: ['alliances'] }),
        ]);
    }

    const createMutation = useMutation({
        mutationFn: (input: { name: string; tag: string; description: string }) => createAlliance(input),
        onSuccess: async () => {
            setCreateError(null);
            navigate('/alliance');
            setTab('overview');
            await refreshAll();
        },
        onError: (error) => setCreateError(errorMessage(error, 'Failed to found the alliance.')),
    });

    async function handleApply(allianceId: string, message: string) {
        // Closed AFTER the call, not before: leaving `applyTarget` set for the
        // duration is what makes the modal's own `isSubmitting` state
        // reachable. On failure it closes too — the message is surfaced by the
        // page-level banner rather than inside a dialog that is going away.
        await runAction(allianceId, () => applyToAlliance(allianceId, message), 'Failed to apply.');
        setApplyTarget(null);
        await refreshAll();
    }

    async function handleCancelApplication(allianceId: string) {
        await runAction(allianceId, () => cancelAllianceApplication(allianceId), 'Failed to cancel the application.');
        await refreshAll();
    }

    async function handleRespond(applicantId: string, accept: boolean) {
        await runAction(
            applicantId,
            () => respondToAllianceApplication(applicantId, accept),
            'Failed to respond to the application.',
        );
        await refreshAll();
    }

    function handleKick(member: AllianceMemberView) {
        openDialog({
            title: `Kick ${member.displayName}?`,
            message: 'They will lose alliance membership immediately and can re-apply later.',
            confirmLabel: 'Kick',
            danger: true,
            onConfirm: async () => {
                await runAction(member.userId, () => kickAllianceMember(member.userId), 'Failed to kick the member.');
                await refreshAll();
            },
        });
    }

    async function handleSetRole(member: AllianceMemberView, role: 'officer' | 'member') {
        await runAction(
            member.userId,
            () => setAllianceMemberRole(member.userId, role),
            'Failed to change the member role.',
        );
        await refreshAll();
    }

    function handleTransferLeadership(member: AllianceMemberView) {
        openDialog({
            title: `Make ${member.displayName} the leader?`,
            message: 'You will step down to Officer. This cannot be undone by you alone.',
            confirmLabel: 'Transfer leadership',
            danger: true,
            onConfirm: async () => {
                await runAction(
                    member.userId,
                    () => transferAllianceLeadership(member.userId),
                    'Failed to transfer leadership.',
                );
                await refreshAll();
            },
        });
    }

    function handleLeave() {
        // A leader leaving a still-populated alliance no longer has to
        // transfer leadership first — it auto-transfers to whoever has the
        // most points (2026-08-15 ruling).
        const isLeaderWithOthers = viewerRole === 'leader' && (detail?.alliance.memberCount ?? 0) > 1;
        openDialog({
            title: 'Leave this alliance?',
            message: isLeaderWithOthers
                ? 'Leadership will automatically transfer to the member with the most points. You can apply again later, but your membership and role are lost.'
                : 'You can apply again later, but your membership and role are lost.',
            confirmLabel: 'Leave',
            danger: true,
            onConfirm: async () => {
                await runAction('leave', () => leaveAlliance(), 'Failed to leave the alliance.');
                navigate('/alliance');
                setTab('overview');
                await refreshAll();
            },
        });
    }

    function handleDisband() {
        openDialog({
            title: 'Disband this alliance?',
            message: 'Every member is removed and the alliance is deleted permanently.',
            confirmLabel: 'Disband',
            danger: true,
            onConfirm: async () => {
                await runAction('disband', () => disbandAlliance(), 'Failed to disband the alliance.');
                navigate('/alliance');
                setTab('overview');
                await refreshAll();
            },
        });
    }

    async function handleSaveDescription(description: string) {
        await runAction('profile', () => updateAlliance({ description }), 'Failed to save the description.');
        setDescriptionDraft(null);
        await refreshAll();
    }

    async function handleToggleRecruiting(recruiting: boolean) {
        await runAction('profile', () => updateAlliance({ recruiting }), 'Failed to change the recruiting setting.');
        await refreshAll();
    }

    const tabs: { key: Tab; label: string; visible: boolean }[] = [
        { key: 'overview', label: 'Overview', visible: true },
        { key: 'members', label: 'Members', visible: detail != null },
        {
            key: 'applications',
            label: `Applications${detail?.applications?.length ? ` (${detail.applications.length})` : ''}`,
            visible: canReviewApplications,
        },
        { key: 'teamPlanet', label: 'Team Planet', visible: isMyAlliance },
        { key: 'suns', label: 'Suns', visible: isMyAlliance },
        { key: 'browse', label: 'Browse', visible: true },
    ];

    return (
        <main className="max-w-5xl mx-auto p-4 flex flex-col gap-4">
            <Panel variant="solid" padding="md" className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-display text-content-primary">Alliance</h2>
                <nav className="flex gap-2 flex-wrap">
                    {tabs.filter((t) => t.visible).map((t) => (
                        <Button
                            key={t.key}
                            type="button"
                            variant={t.key === tab ? 'primary' : 'secondary'}
                            size="sm"
                            aria-current={t.key === tab ? 'page' : undefined}
                            data-tutorial-id={t.key === 'teamPlanet' ? 'alliance-team-planet' : undefined}
                            onClick={() => setTab(t.key)}
                        >
                            {t.label}
                        </Button>
                    ))}
                </nav>
            </Panel>

            {actionError && (
                <Panel variant="solid" padding="md" className="border-status-danger/40">
                    <p className="text-status-danger" role="alert">{actionError}</p>
                </Panel>
            )}

            {/*
              * Keyed off membership, not merely off the route: `/alliance/:id`
              * is how the profile card and the ranking table link to an
              * alliance, INCLUDING the viewer's own, so `previewAllianceId`
              * alone would tell a leader they are "viewing another alliance"
              * on the normal path to their own roster.
              */}
            {previewAllianceId && !isMyAlliance && (
                <Panel variant="solid" padding="sm" className="flex items-center justify-between gap-3">
                    <p className="text-content-muted text-label">You are viewing another alliance.</p>
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate('/alliance')}
                    >
                        Back to my alliance
                    </Button>
                </Panel>
            )}

            {tab === 'overview' && (
                <Panel variant="solid" padding="md">
                    {allianceQuery.isPending ? (
                        <p className="text-content-muted animate-pulse">Loading alliance…</p>
                    ) : allianceQuery.isError ? (
                        <p className="text-status-danger">{errorMessage(allianceQuery.error, 'Failed to load the alliance.')}</p>
                    ) : !detail ? (
                        <div className="flex flex-col gap-6" data-tutorial-id="alliance-join">
                            <div>
                                <h3 className="text-title text-content-primary mb-1">You are not in an alliance</h3>
                                <p className="text-content-muted text-label">
                                    Found your own, or apply to one from the Browse tab.
                                </p>
                            </div>
                            <AllianceCreateForm
                                onCreate={async (input) => {
                                    // `mutateAsync` rejects on failure; the
                                    // message is already surfaced by
                                    // `onError`, so swallow the rejection
                                    // rather than letting it escape as an
                                    // unhandled promise.
                                    await createMutation.mutateAsync(input).catch(() => undefined);
                                }}
                                isSubmitting={createMutation.isPending}
                                error={createError}
                            />
                        </div>
                    ) : (
                        <div className="flex flex-col gap-4" data-testid="alliance-overview">
                            <div>
                                <h3 className="text-display text-content-primary">
                                    <span className="text-content-muted">[{detail.alliance.tag}]</span> {detail.alliance.name}
                                </h3>
                                <p className="text-content-muted text-label inline-flex items-center gap-1 flex-wrap">
                                    <span>Founded {detail.alliance.createdAt.toLocaleDateString()} · Leader</span>
                                    <AllianceLeaderIcon />
                                    <Link
                                        to={`/player/${detail.alliance.leaderId}`}
                                        className="text-content-secondary hover:text-content-primary hover:underline"
                                    >
                                        {detail.alliance.leaderName || '—'}
                                    </Link>
                                    {viewerRole && (
                                        <span className="inline-flex items-center gap-1">
                                            · You are{' '}
                                            <Badge tone={viewerRole === 'leader' ? 'accent' : viewerRole === 'officer' ? 'info' : 'neutral'}>
                                                {ALLIANCE_ROLE_LABELS[viewerRole]}
                                            </Badge>
                                        </span>
                                    )}
                                </p>
                            </div>

                            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                <div className="p-3 bg-surface-raised rounded-chip border border-space-700">
                                    <dt className="text-content-muted text-micro">Members</dt>
                                    <dd className="text-title text-content-primary">
                                        {detail.alliance.memberCount} / {ALLIANCE_MAX_MEMBERS}
                                    </dd>
                                </div>
                                <div className="p-3 bg-surface-raised rounded-chip border border-space-700" data-tutorial-id="alliance-sun-count">
                                    <dt className="text-content-muted text-micro">Suns controlled</dt>
                                    <dd className="text-title text-content-primary" data-testid="alliance-sun-count">
                                        {detail.alliance.sunCountIsMinimum
                                            ? `${detail.alliance.sunCount} or more`
                                            : detail.alliance.sunCount}
                                    </dd>
                                </div>
                                <div className="p-3 bg-surface-raised rounded-chip border border-space-700">
                                    <dt className="text-content-muted text-micro">Alliance points</dt>
                                    <dd className="text-title text-content-primary" data-testid="alliance-total-points">
                                        {formatFullNumber(detail.alliance.totalPoints)}
                                    </dd>
                                </div>
                                <div className="p-3 bg-surface-raised rounded-chip border border-space-700">
                                    <dt className="text-content-muted text-micro">Battle points</dt>
                                    <dd className="text-title text-content-primary" data-testid="alliance-battle-points">
                                        {formatFullNumber(detail.alliance.battlePoints)}
                                    </dd>
                                </div>
                                <div className="p-3 bg-surface-raised rounded-chip border border-space-700">
                                    <dt className="text-content-muted text-micro">Resource points</dt>
                                    <dd className="text-title text-content-primary" data-testid="alliance-resource-points">
                                        {formatFullNumber(detail.alliance.resourcePoints)}
                                    </dd>
                                </div>
                                <div className="p-3 bg-surface-raised rounded-chip border border-space-700">
                                    <dt className="text-content-muted text-micro">Recruiting</dt>
                                    <dd className="text-title text-content-primary">
                                        {detail.alliance.recruiting ? 'Open' : 'Closed'}
                                    </dd>
                                </div>
                            </dl>

                            <section className="flex flex-col gap-2">
                                <h4 className="text-content-muted text-label font-medium">Description</h4>
                                {descriptionDraft === null ? (
                                    <>
                                        <p className="text-content-secondary whitespace-pre-wrap">
                                            {detail.alliance.description || 'No description yet.'}
                                        </p>
                                        {canEditProfile && (
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => setDescriptionDraft(detail.alliance.description)}
                                            >
                                                Edit description
                                            </Button>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <textarea
                                            className={cn(FIELD_BASE_CLASSES, FIELD_FOCUS_CLASSES, 'h-auto min-h-24 py-2')}
                                            value={descriptionDraft}
                                            maxLength={ALLIANCE_DESCRIPTION_MAX_LENGTH}
                                            aria-label="Alliance description"
                                            onChange={(e) => setDescriptionDraft(e.target.value)}
                                        />
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                variant="primary"
                                                size="sm"
                                                loading={busyId === 'profile'}
                                                onClick={() => void handleSaveDescription(descriptionDraft)}
                                            >
                                                Save
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => setDescriptionDraft(null)}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </section>

                            {isMyAlliance && (
                                <div className="flex gap-2 flex-wrap border-t border-hairline pt-4">
                                    {canEditProfile && (
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            size="sm"
                                            loading={busyId === 'profile'}
                                            onClick={() => void handleToggleRecruiting(!detail.alliance.recruiting)}
                                        >
                                            {detail.alliance.recruiting ? 'Close recruitment' : 'Open recruitment'}
                                        </Button>
                                    )}
                                    {hasAlliancePermission(viewerRole, 'disband') && (
                                        <Button
                                            type="button"
                                            variant="danger"
                                            size="sm"
                                            onClick={handleDisband}
                                        >
                                            Disband alliance
                                        </Button>
                                    )}
                                    {(!hasAlliancePermission(viewerRole, 'disband') || detail.alliance.memberCount > 1) && (
                                        <Button
                                            type="button"
                                            variant="danger"
                                            size="sm"
                                            onClick={handleLeave}
                                        >
                                            Leave alliance
                                        </Button>
                                    )}
                                </div>
                            )}

                            {!isMyAlliance && (
                                <p className="text-content-muted text-label border-t border-hairline pt-4">
                                    {detail.viewerApplicationPending
                                        ? 'Your application to this alliance is pending.'
                                        : 'Apply from the Browse tab to join this alliance.'}
                                </p>
                            )}
                        </div>
                    )}
                </Panel>
            )}

            {tab === 'members' && detail && (
                <Panel variant="solid" padding="md">
                    <AllianceMemberTable
                        members={detail.members}
                        viewerId={profile?.id ?? ''}
                        viewerRole={viewerRole}
                        onKick={handleKick}
                        onSetRole={(member, role) => void handleSetRole(member, role)}
                        onTransferLeadership={handleTransferLeadership}
                        busyMemberId={busyId}
                    />
                </Panel>
            )}

            {tab === 'applications' && canReviewApplications && (
                <Panel variant="solid" padding="md">
                    <AllianceApplicationList
                        applications={detail?.applications ?? []}
                        onRespond={(applicantId, accept) => void handleRespond(applicantId, accept)}
                        busyApplicantId={busyId}
                    />
                </Panel>
            )}

            {tab === 'teamPlanet' && isMyAlliance && (
                <AllianceTeamPlanetTab viewerRole={viewerRole} />
            )}

            {tab === 'suns' && isMyAlliance && (
                <AllianceSunsTab viewerRole={viewerRole} />
            )}

            {tab === 'browse' && (
                <Panel variant="solid" padding="md">
                    <AllianceBrowseList
                        alliances={browseQuery.data?.alliances ?? []}
                        myAllianceId={browseQuery.data?.myAllianceId ?? null}
                        pendingApplicationIds={browseQuery.data?.pendingApplicationIds ?? []}
                        search={search}
                        onSearchChange={setSearch}
                        onApply={(alliance) => setApplyTarget(alliance)}
                        onCancelApplication={(id) => void handleCancelApplication(id)}
                        onOpen={(id) => {
                            navigate(`/alliance/${id}`);
                            setTab('overview');
                        }}
                        isPending={browseQuery.isPending}
                        busyAllianceId={busyId}
                    />
                </Panel>
            )}

            <AllianceApplyModal
                alliance={applyTarget}
                isSubmitting={applyTarget != null && busyId === applyTarget.id}
                onSubmit={(allianceId, message) => void handleApply(allianceId, message)}
                onCancel={() => setApplyTarget(null)}
            />
        </main>
    );
}
